interface WorkerResponse {
  type: 'ready' | 'frame-complete' | 'complete' | 'error';
  requestId: number;
  bytes?: ArrayBuffer;
  message?: string;
}

interface PendingRequest {
  resolve: (value: ArrayBuffer | undefined) => void;
  reject: (error: Error) => void;
}

export class GifEncoderClient {
  private readonly worker: Worker;
  private readonly pending = new Map<number, PendingRequest>();
  private nextRequestId = 1;

  constructor() {
    this.worker = new Worker(new URL('./gifEncoder.worker.ts', import.meta.url), {
      type: 'module'
    });

    this.worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
      const response = event.data;
      const pending = this.pending.get(response.requestId);
      if (!pending) return;

      this.pending.delete(response.requestId);
      if (response.type === 'error') {
        pending.reject(new Error(response.message || 'GIF encoding failed'));
      } else {
        pending.resolve(response.bytes);
      }
    };

    this.worker.onerror = (event) => {
      const error = new Error(event.message || 'GIF encoder worker failed');
      this.pending.forEach(({ reject }) => reject(error));
      this.pending.clear();
    };
  }

  async initialize(): Promise<void> {
    await this.send({ type: 'init' });
  }

  async addFrame(
    rgba: Uint8ClampedArray,
    width: number,
    height: number,
    delayMs: number,
    maxColors: number,
    repeat: number
  ): Promise<void> {
    const buffer = rgba.buffer.slice(
      rgba.byteOffset,
      rgba.byteOffset + rgba.byteLength
    ) as ArrayBuffer;
    await this.send(
      { type: 'frame', rgba: buffer, width, height, delayMs, maxColors, repeat },
      [buffer]
    );
  }

  async finish(): Promise<Uint8Array> {
    const bytes = await this.send({ type: 'finish' });
    if (!bytes) throw new Error('GIF encoder returned no data');
    return new Uint8Array(bytes);
  }

  dispose(): void {
    this.worker.terminate();
    const error = new Error('GIF encoding was cancelled');
    this.pending.forEach(({ reject }) => reject(error));
    this.pending.clear();
  }

  private send(
    message: Record<string, unknown>,
    transfer: Transferable[] = []
  ): Promise<ArrayBuffer | undefined> {
    const requestId = this.nextRequestId++;
    return new Promise((resolve, reject) => {
      this.pending.set(requestId, { resolve, reject });
      this.worker.postMessage({ ...message, requestId }, transfer);
    });
  }
}
